<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import {
        ChevronRightIcon,
        MessageCircleIcon,
        MessageSquarePlusIcon,
        PencilIcon,
    } from '@lucide/vue';
    import { computed, ref, nextTick, watch } from 'vue';
    import EchoButton from '../EchoButton.vue';
    import { FilterSelection } from '@/js/services/api/widget/types';
    import { createFilterSelectionString } from '@/js/utils/widgetUtils';
    import ParsedMarkdown from '../ParsedMarkdown.vue';

    type Props = {
        filterSelection?: FilterSelection;
        comment?: string;
        isStatic?: boolean;
    };

    const props = defineProps<Props>();

    const emit = defineEmits<{
        (e: 'updateComment', value: string): void;
    }>();

    const comment_model = ref<string>(props.comment || '');

    const edit_comment = ref<boolean>(false);

    const hide_comment = ref<boolean>(true);

    const showAllFilters = ref<boolean>(false);

    const filter_text = computed(
        () => props.filterSelection && createFilterSelectionString(props.filterSelection)
    );

    const submit = () => {
        emit('updateComment', comment_model.value);
        edit_comment.value = false;
    };

    const handleCommentClick = () => {
        if (props.isStatic || !!comment_model.value) {
            hide_comment.value = !hide_comment.value;
        } else {
            edit_comment.value = !edit_comment.value;
        }
    };

    const commentPreview = {
        maxHeight: '2.5rem',
        overflow: 'hidden',
    };

    const commentTextarea = ref<HTMLTextAreaElement | null>(null);

    const resizeTextarea = async () => {
        await nextTick();
        const el = commentTextarea.value;
        if (!el) return;

        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    };

    watch(edit_comment, async (isEditing) => {
        if (isEditing) {
            await nextTick();
            resizeTextarea();
        }
    });
</script>
<template>
    <div
        v-if="!isStatic || !!filter_text || !!comment"
        class="flex flex-col gap-2 pt-5 pb-6 px-7 border-t-1 border-gray-lightest"
    >
        <button
            v-if="filter_text"
            :aria-label="__('widget-show-filters')"
            @click="showAllFilters = !showAllFilters"
        >
            <p
                :class="[
                    'text-body text-left',
                    !showAllFilters && 'overflow-ellipsis overflow-hidden whitespace-nowrap',
                ]"
            >
                <span class="font-medium">{{ __('widget-filter') }}</span>
                {{ ` ${filter_text}` }}
            </p>
        </button>
        <div v-else class="h-5" />
        <div v-if="!isStatic">
            <div v-if="edit_comment" class="flex flex-col gap-2 items-end">
                <textarea
                    ref="commentTextarea"
                    v-model="comment_model"
                    class="w-full border-0 text-sm border-rounded-lg resize-none"
                    rows="2"
                    :placeholder="__('widget-comment-placeholder')"
                    @input="resizeTextarea"
                />
                <div>
                    <EchoButton @click="submit()">
                        {{ __('done') }}
                    </EchoButton>
                </div>
            </div>
            <div v-else>
                <div class="h-5 flex flex-row justify-start items-center gap-1">
                    <div class="mr-1">
                        <MessageCircleIcon v-if="comment_model" />
                        <MessageSquarePlusIcon v-else />
                    </div>
                    <button
                        class="text-body-bold underline cursor-pointer"
                        @click="handleCommentClick"
                    >
                        {{ comment_model ? __('widget-comment') : __('widget-comment-hint') }}
                    </button>
                    <button
                        v-if="comment_model"
                        :aria-label="__('widget-hide-comment')"
                        class="cursor-pointer"
                        @click="hide_comment = !hide_comment"
                    >
                        <ChevronRightIcon :class="!hide_comment ? 'rotate-270' : 'rotate-90'" />
                    </button>
                    <button
                        v-if="comment_model"
                        :aria-label="__('widget-edit-comment')"
                        class="h-5 w-5 cursor-pointer"
                        @click="edit_comment = true"
                    >
                        <PencilIcon />
                    </button>
                </div>
                <div class="max-h-40 overflow-y-auto">
                    <ParsedMarkdown
                        v-if="comment_model"
                        class="text-body mt-1"
                        :markdown="comment_model"
                        :class="{ 'widget-comment-collapsed': hide_comment }"
                        :style="hide_comment ? commentPreview : undefined"
                        :breaks="true"
                    />
                </div>
            </div>
        </div>
        <div v-else-if="isStatic && comment">
            <div class="h-5 flex flex-row justify-start items-center gap-1">
                <div class="mr-1">
                    <MessageCircleIcon />
                </div>
                <button class="text-body-bold underline cursor-pointer" @click="handleCommentClick">
                    {{ __('widget-comment') }}
                </button>
                <button
                    v-if="comment_model"
                    :aria-label="__('widget-hide-comment')"
                    class="cursor-pointer"
                    @click="hide_comment = !hide_comment"
                >
                    <ChevronRightIcon :class="!hide_comment ? 'rotate-270' : 'rotate-90'" />
                </button>
            </div>
            <div class="max-h-40 overflow-y-auto">
                <ParsedMarkdown
                    v-if="comment_model"
                    class="text-body mt-1"
                    :markdown="comment_model"
                    :class="{ 'widget-comment-collapsed': hide_comment }"
                    :style="hide_comment ? commentPreview : undefined"
                    :breaks="true"
                />
            </div>
        </div>
    </div>
</template>
